/**
 * Standardized React Query defaults to ensure consistent behavior across the application
 * Prevents individual hooks from "breaking rhythm" with different configurations
 */

import { UseQueryOptions, UseMutationOptions, UseInfiniteQueryOptions } from '@tanstack/react-query';

// ==================== STANDARD RETRY STRATEGIES ====================

export const createRetryStrategy = (maxRetries: number = 2) => {
  return (failureCount: number, error: any) => {
    // Don't retry on auth errors
    if (error?.message?.includes('auth') || error?.status === 401 || error?.status === 403) {
      return false;
    }
    
    // Don't retry on client errors (4xx except auth)
    if (error?.status >= 400 && error?.status < 500 && error?.status !== 401 && error?.status !== 403) {
      return false;
    }
    
    // Don't retry on circuit breaker errors
    if (error?.message?.includes('Circuit breaker')) {
      return false;
    }
    
    return failureCount < maxRetries;
  };
};

export const createRetryDelay = (baseDelay: number = 1000) => {
  return (attemptIndex: number) => Math.min(baseDelay * 2 ** attemptIndex, 30000);
};

// ==================== QUERY TYPE CONFIGURATIONS ====================

// Standard configuration for auth-related queries
export const AUTH_QUERY_DEFAULTS = {
  staleTime: 10 * 60 * 1000,     // 10 minutes - auth data changes infrequently
  gcTime: 30 * 60 * 1000,        // 30 minutes - keep auth data longer
  retry: createRetryStrategy(1),  // Reduced retries for auth
  retryDelay: createRetryDelay(1500),
  refetchOnWindowFocus: false,    // Prevent excessive auth checks
  refetchOnReconnect: false,      // Prevent auth spam on reconnect
  refetchOnMount: false,          // Only fetch when needed
} as const;

// Standard configuration for data queries (projects, items, etc.)
export const DATA_QUERY_DEFAULTS = {
  staleTime: 5 * 60 * 1000,      // 5 minutes - data changes moderately
  gcTime: 10 * 60 * 1000,        // 10 minutes - standard garbage collection
  retry: createRetryStrategy(2),  // Standard retry count
  retryDelay: createRetryDelay(1000),
  refetchOnWindowFocus: false,    // Prevent focus spam
  refetchOnReconnect: true,       // Refetch on network reconnect
  refetchOnMount: false,          // Use cache first
} as const;

// Standard configuration for real-time/frequently changing data
export const REALTIME_QUERY_DEFAULTS = {
  staleTime: 30 * 1000,          // 30 seconds - data changes frequently
  gcTime: 2 * 60 * 1000,         // 2 minutes - shorter cache for real-time
  retry: createRetryStrategy(3),  // More retries for real-time
  retryDelay: createRetryDelay(500),
  refetchOnWindowFocus: true,     // Refetch on focus for real-time
  refetchOnReconnect: true,       // Important for real-time
  refetchOnMount: true,           // Always fresh for real-time
} as const;

// Standard configuration for heavy/expensive queries (reports, analytics)
export const HEAVY_QUERY_DEFAULTS = {
  staleTime: 15 * 60 * 1000,     // 15 minutes - expensive data cached longer
  gcTime: 60 * 60 * 1000,        // 1 hour - keep expensive data very long
  retry: createRetryStrategy(1),  // Fewer retries for expensive operations
  retryDelay: createRetryDelay(3000),
  refetchOnWindowFocus: false,    // Never refetch expensive queries on focus
  refetchOnReconnect: false,      // Manual refetch only
  refetchOnMount: false,          // Use cache aggressively
} as const;

// Standard configuration for user-specific data (diary, preferences)
export const USER_QUERY_DEFAULTS = {
  staleTime: 3 * 60 * 1000,      // 3 minutes - user data changes regularly
  gcTime: 15 * 60 * 1000,        // 15 minutes - medium cache duration
  retry: createRetryStrategy(2),  // Standard retry
  retryDelay: createRetryDelay(1000),
  refetchOnWindowFocus: false,    // Don't spam user data
  refetchOnReconnect: true,       // Sync user data on reconnect
  refetchOnMount: false,          // Use cache first
} as const;

// ==================== MUTATION CONFIGURATIONS ====================

export const STANDARD_MUTATION_DEFAULTS = {
  retry: createRetryStrategy(1),  // Single retry for mutations
  retryDelay: createRetryDelay(2000),
} as const;

export const CRITICAL_MUTATION_DEFAULTS = {
  retry: createRetryStrategy(2),  // More retries for critical operations
  retryDelay: createRetryDelay(1500),
} as const;

// ==================== HELPER FUNCTIONS ====================

/**
 * Create standardized query options based on query type
 */
export function createStandardQueryOptions<T>(
  type: 'auth' | 'data' | 'realtime' | 'heavy' | 'user',
  overrides?: Partial<UseQueryOptions<T>>
): Partial<UseQueryOptions<T>> {
  const baseDefaults = {
    auth: AUTH_QUERY_DEFAULTS,
    data: DATA_QUERY_DEFAULTS,
    realtime: REALTIME_QUERY_DEFAULTS,
    heavy: HEAVY_QUERY_DEFAULTS,
    user: USER_QUERY_DEFAULTS,
  }[type];

  return {
    ...baseDefaults,
    ...overrides,
  };
}

/**
 * Create standardized mutation options based on criticality
 */
export function createStandardMutationOptions<T, TVariables>(
  critical: boolean = false,
  overrides?: Partial<UseMutationOptions<T, any, TVariables>>
): Partial<UseMutationOptions<T, any, TVariables>> {
  const baseDefaults = critical ? CRITICAL_MUTATION_DEFAULTS : STANDARD_MUTATION_DEFAULTS;

  return {
    ...baseDefaults,
    ...overrides,
  };
}

/**
 * Create standardized infinite query options
 */
export function createStandardInfiniteQueryOptions<T>(
  type: 'data' | 'realtime' | 'heavy',
  overrides?: Partial<UseInfiniteQueryOptions<T>>
): Partial<UseInfiniteQueryOptions<T>> {
  const baseDefaults = {
    data: DATA_QUERY_DEFAULTS,
    realtime: REALTIME_QUERY_DEFAULTS,
    heavy: HEAVY_QUERY_DEFAULTS,
  }[type];

  return {
    ...baseDefaults,
    // Infinite queries should keep previous data to prevent loading flickers
    placeholderData: (previousData) => previousData,
    ...overrides,
  };
}

// ==================== QUERY KEY UTILITIES ====================

/**
 * Standardized query key generation with namespace support
 */
export class QueryKeyFactory {
  static auth = {
    all: ['auth'] as const,
    user: (userId?: string) => [...QueryKeyFactory.auth.all, 'user', userId] as const,
    roles: (userId?: string) => [...QueryKeyFactory.auth.all, 'roles', userId] as const,
    permissions: (userId?: string, permission?: string) => 
      [...QueryKeyFactory.auth.all, 'permissions', userId, permission] as const,
    admin: (userId?: string) => [...QueryKeyFactory.auth.all, 'admin', userId] as const,
  };

  static projects = {
    all: ['projects'] as const,
    lists: () => [...QueryKeyFactory.projects.all, 'list'] as const,
    list: (filters?: Record<string, any>) => 
      [...QueryKeyFactory.projects.lists(), filters] as const,
    details: () => [...QueryKeyFactory.projects.all, 'detail'] as const,
    detail: (id: string) => [...QueryKeyFactory.projects.details(), id] as const,
    permissions: (id: string, userId?: string) => 
      [...QueryKeyFactory.projects.detail(id), 'permissions', userId] as const,
  };

  static diary = {
    all: ['diary'] as const,
    project: (projectId: string) => [...QueryKeyFactory.diary.all, projectId] as const,
    photos: (projectId: string, date?: string) => 
      [...QueryKeyFactory.diary.project(projectId), 'photos', date] as const,
    notes: (projectId: string, date?: string) => 
      [...QueryKeyFactory.diary.project(projectId), 'notes', date] as const,
    pins: (projectId: string) => [...QueryKeyFactory.diary.project(projectId), 'pins'] as const,
    floorplan: (projectId: string) => 
      [...QueryKeyFactory.diary.project(projectId), 'floorplan'] as const,
  };

  static specs = {
    all: ['specs'] as const,
    project: (projectId: string) => [...QueryKeyFactory.specs.all, projectId] as const,
    data: (projectId: string, filters?: Record<string, any>) => 
      [...QueryKeyFactory.specs.project(projectId), 'data', filters] as const,
    pins: (projectId: string) => [...QueryKeyFactory.specs.project(projectId), 'pins'] as const,
    layers: (projectId: string) => 
      [...QueryKeyFactory.specs.project(projectId), 'layers'] as const,
  };

  static items = {
    all: ['items'] as const,
    project: (projectId: string) => [...QueryKeyFactory.items.all, projectId] as const,
    list: (projectId: string, filters?: Record<string, any>) => 
      [...QueryKeyFactory.items.project(projectId), 'list', filters] as const,
    categories: () => [...QueryKeyFactory.items.all, 'categories'] as const,
  };
}

// ==================== CACHE INVALIDATION UTILITIES ====================

/**
 * Standardized cache invalidation patterns
 */
export class CacheInvalidator {
  static auth = {
    all: () => QueryKeyFactory.auth.all,
    user: (userId?: string) => QueryKeyFactory.auth.user(userId),
    userRelated: (userId?: string) => [
      QueryKeyFactory.auth.user(userId),
      QueryKeyFactory.auth.roles(userId),
      QueryKeyFactory.auth.admin(userId),
    ],
  };

  static projects = {
    all: () => QueryKeyFactory.projects.all,
    lists: () => QueryKeyFactory.projects.lists(),
    detail: (id: string) => QueryKeyFactory.projects.detail(id),
    related: (id: string) => [
      QueryKeyFactory.projects.detail(id),
      QueryKeyFactory.projects.lists(),
    ],
  };

  static diary = {
    all: (projectId: string) => QueryKeyFactory.diary.project(projectId),
    photos: (projectId: string) => QueryKeyFactory.diary.photos(projectId),
    related: (projectId: string) => [
      QueryKeyFactory.diary.project(projectId),
      QueryKeyFactory.projects.detail(projectId),
    ],
  };
}

// ==================== EXPORTS ====================

export {
  QueryKeyFactory as standardQueryKeys,
  CacheInvalidator as standardCacheInvalidation,
};
